"use client";

import { CarouselTabItem, CarouselTabs } from "@/components/common/carousel-tabs";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { m } from "framer-motion";
import { Building, Home } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { BuyerJourneyPhases } from "./buyer-journey";
import { ProcessFlow } from "./process-flow";
import { SellerJourneyPhases } from "./seller-journey";
import { HowItWorksProps } from "./types";

const tabs: CarouselTabItem[] = [
	{
		id: "buy",
		label: "Buy Property",
		icon: <Home className="w-5 h-5" />,
		content: <ProcessFlow phases={BuyerJourneyPhases} />,
	},
	{
		id: "sell",
		label: "Sell Property",
		icon: <Building className="w-5 h-5" />,
		content: <ProcessFlow phases={SellerJourneyPhases} />,
	},
];

export const HowItWorks: React.FC<HowItWorksProps> = ({ defaultTab = "buy", relationship }) => {
	const { ref: titleRef, isInView: titleInView } = useScrollAnimation<HTMLHeadingElement>({
		threshold: 0.8,
		triggerOnce: true,
	});
	const { ref: tabsRef, isInView: tabsInView } = useScrollAnimation<HTMLDivElement>({
		threshold: 0.5,
		triggerOnce: true,
	});
	const searchParams = useSearchParams();
	const tab = searchParams?.get("tab") ?? defaultTab;

	const tabsListed = tabs
		.filter(({ id }) => !relationship || id === relationship)
		.map(({ id, content, ...rest }) => ({
			content: id === tab ? content : null,
			id,
			...rest,
		}));
	return (
		<section className="py-12 md:py-16 bg-background max-w-7xl mx-auto">
			<div className="container px-4 mx-auto max-w-7xl">
				<m.h2
					ref={titleRef}
					className="text-3xl md:text-4xl font-bold text-foreground text-center mb-6 md:mb-8"
					initial={{ opacity: 0, y: 20 }}
					animate={titleInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
					transition={{ duration: 0.4, ease: "easeOut", type: "tween" }}
					style={{ willChange: titleInView ? "transform, opacity" : "auto" }}
				>
					How it works?
				</m.h2>

				{/* Tabs */}
				<m.div
					ref={tabsRef}
					className="flex justify-center mb-8 md:mb-12"
					initial={{ opacity: 0, y: 15 }}
					animate={tabsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 15 }}
					transition={{ duration: 0.4, ease: "easeOut", delay: 0.1, type: "tween" }}
					style={{ willChange: tabsInView ? "transform, opacity" : "auto" }}
				>
					<CarouselTabs
						tabs={tabsListed}
						defaultSelectedKey={defaultTab}
						variant="pills"
						size="md"
						className="w-full"
						itemClassName="min-h-[0px]"
						urlQueryParam="tab"
						usePushState={true}
					/>
				</m.div>
			</div>
		</section>
	);
};
